<template>
    <div v-if="is_finanzbildung_article">
        <component :is="'script'" type="application/ld+json">
            { "@context": "https://schema.org", "@type": "Article", "mainEntityOfPage": { "@type": "WebPage", "@id": "{{ current_url }}" }, "headline": "{{
                $translate(post.title)
            }}", "image": { "@type": "ImageObject", "url": "{{ post.image }}" }, "author": { "@type": "Person", "name": "{{ post.user?.name.toString() }}", "url": "{{
                authorProfileUrl
            }}" }, "datePublished": "{{ post.published_at }}", "description": "{{ $translate(post.teaser) }}", "publisher": { "@type": "Organization", "name": "Hopf-Klinkmüller
            Capital Management GmbH & Co. KG", "logo": { "@type": "ImageObject", "url":
            "https://d178cgmue8nngv.cloudfront.net/24b2357d-58e7-412b-b7ef-699b8d21aaba/images/logo-on-light-background.svg", "width": 351, "height": 104 } } }
        </component>
    </div>

    <div v-else>
        <component :is="'script'" type="application/ld+json">
            { "@context": "https://schema.org", "@type": "NewsArticle", "isAccessibleForFree": true, "headline": "{{ $translate(post.title) }}", "description": "{{
                $translate(post.teaser)
            }}", "mainEntityOfPage": { "@type": "WebPage", "@id": "{{ current_url }}" }, "image": [ "{{ post.image }}" ], "datePublished": "{{ post.published_at }}", "author": {
            "@type": "Person", "name": "{{ post.user?.name.toString() }}", "url": "{{ authorProfileUrl }}" }, "publisher": { "@type": "Organization", "name": "Hopf-Klinkmüller
            Capital Management GmbH & Co. KG", "logo": { "@type": "ImageObject", "url":
            "https://d178cgmue8nngv.cloudfront.net/24b2357d-58e7-412b-b7ef-699b8d21aaba/images/logo-on-light-background.svg" } } }
        </component>
    </div>

    <transition :name="$route.meta.transition || 'fade'">
        <div>
            <Notification v-if="post?.published_at === null" class="bg-red text-white border-b border-gray-300">
                <p>
                    {{ $t('Content is not yet published and is displayed because you are logged in as administrator.') }}
                </p>
            </Notification>

            <TickerTape v-if="tradingView" :options="tickerTapeOptions" />

            <div id="tradingroom-post" class="container mx-auto relative">
                <div id="content" class="flex flex-col lg:flex-row lg:divide-x divide-gray-300 py-4">
                    <div class="lg:w-3/4 lg:px-8 relative">
                        <div id="post" class="min-h-screen relative">
                            <div v-if="post.image" class="rounded-md shadow-md p-8 relative h-[500px] z-10">
                                <div
                                    :style="`background-image: url(${post.image_lg ?? post.image})`"
                                    class="bg-cover bg-no-repeat bg-center absolute top-0 left-0 w-full h-full group-hover:scale-110 transition-all ease-in-out duration-500 rounded-md"
                                ></div>
                                <div class="overlay-bottom-dark absolute top-0 bottom-0 left-0 right-0 w-full h-full z-[5] rounded-md" />

                                <div class="absolute top-0 left-0 w-full h-full p-4 flex items-end text-white z-[10] group-hover:backdrop-blur-sm group-hover:bg-blue-dark/30">
                                    <div class="w-full">
                                        <div class="flex flex-col lg:flex-row lg:items-end lg:justify-between">
                                            <h1 class="text-2xl font-bold">
                                                {{ $translate(post.title) }}
                                            </h1>

                                            <small class="block z-[10] text-white shrink-0">
                                                {{ formatDateByRegion($moment(post.published_at)) }}
                                            </small>
                                        </div>

                                        <hr class="border-t border-white my-4" />

                                        <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center">
                                            <router-link
                                                v-if="post.user"
                                                :to="'/hkcmnews/?author=' + post.user.id + '&author_name=' + post.user.name"
                                                class="flex flex-row gap-2 items-center mb-4"
                                            >
                                                <img v-if="post.user.image" class="rounded-full w-12 h-12 object-cover object-center" :src="post.user.image" />
                                                <div>
                                                    <p>{{ $t('plain_from') }}: {{ post.user.name }}</p>
                                                </div>
                                            </router-link>

                                            <div v-if="post.postCategories.data.length" class="flex flex-row">
                                                <div
                                                    v-for="category in post.postCategories.data.slice(0, 5)"
                                                    :key="category"
                                                    class="rounded-full bg-blue-light p-1.5 flex flex-row gap-2 items-center"
                                                >
                                                    <div class="w-2 h-2 rounded-full bg-blue" />
                                                    <span class="text-xxs text-blue-dark">
                                                        {{ $translate(category.name) }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div :class="post.image ? 'mt-8' : 'mt-0'" class="flex flex-col lg:flex-row lg:divide-x divide-gray-300">
                                <div class="order-2 lg:order-1 lg:w-1/4 lg:pr-8">
                                    <SidebarListLeft v-if="post?.postTags?.data" :except="post.id" :tags="post.postTags.data" />
                                </div>
                                <div class="order-1 lg:order-2 lg:w-3/4 lg:pl-8">
                                    <h1 v-if="!post.image" class="text-4xl font-bold mb-4">
                                        {{ $translate(post.title) }}
                                    </h1>
                                    <div class="content fr-view" v-html="$translate(post.content)" />

                                    <div v-if="post.user">
                                        <AuthorBox :name="post.user.name" preview :authors="authors" class="mt-32 mb-20" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="sticky bottom-4 flex items-end flex-col gap-4 z-40 mt-8 mb-4">
                            <TransitionExpand>
                                <div v-if="toggleSocial" class="flex justify-end">
                                    <div class="p-2 bg-white text-blue-dark shadow-lg rounded-md flex flex-col gap-2 w-max border-dotted border-2 border-gray-500">
                                        <ShareNetwork network="twitter" :url="url" :title="$translate(post.title)" :description="$translate(post.teaser)">
                                            <FontAwesomeIcon :icon="['fab', 'twitter']" />
                                            {{ $t('Share on Twitter') }}
                                        </ShareNetwork>
                                        <ShareNetwork network="linkedin" :url="url" :title="$translate(post.title)">
                                            <FontAwesomeIcon :icon="['fab', 'linkedin']" />
                                            {{ $t('Share on LinkedIn') }}
                                        </ShareNetwork>
                                        <ShareNetwork network="facebook" :url="url" :title="$translate(post.title)" :description="$translate(post.teaser)">
                                            <FontAwesomeIcon :icon="['fab', 'facebook']" />
                                            {{ $t('Share on Facebook') }}
                                        </ShareNetwork>
                                        <ShareNetwork
                                            network="email"
                                            :url="url"
                                            :title="$translate('HKCM - Empfohlener Artikel aus unseren HKCM News - ') + $translate(post.title)"
                                            :description="'\n' + $translate('Hier ist ein interessanter Artikel aus den HKCM-News:') + '\n\n' + $translate(post.teaser)"
                                        >
                                            <FontAwesomeIcon :icon="['fas', 'envelope']" />
                                            {{ $t('Share via Mail') }}
                                        </ShareNetwork>
                                        <span class="cursor-pointer" @click="copy_link()">
                                            <FontAwesomeIcon :icon="['fas', 'link']" />
                                            {{ $t('Copy Link') }}
                                        </span>
                                        <input ref="copy_link" type="text" class="h-0 opacity-0" :value="current_url" />
                                    </div>
                                </div>
                            </TransitionExpand>

                            <div class="p-2 bg-white text-blue-dark shadow-md rounded-md flex flex-row w-max">
                                <div
                                    class="bg-blue-light rounded-md shadow-md transition-all ease-in-out duration-200 pointer-events-none"
                                    :class="currentScrollPercentage > 0 ? 'opacity-1 w-[76px] h-[76px] mr-2 p-2' : 'opacity-0 w-0 h-0'"
                                >
                                    <ProgressCircle :progress="currentScrollPercentage" />
                                </div>
                                <div
                                    class="bg-blue-light rounded-md p-2 shadow-md transition-all ease-in-out duration-200 w-[76px] h-[76px] flex flex-col gap-2 items-center justify-center cursor-pointer"
                                    @click="toggleSocial = !toggleSocial"
                                >
                                    <Icon type="Share" viewBox="0 0 24 24" width="24" height="24" />
                                    <span class="uppercase text-xxs">
                                        {{ $t('Share') }}
                                    </span>
                                </div>
                                <div
                                    class="bg-blue-light rounded-md p-2 ml-2 shadow-md transition-all ease-in-out duration-200 w-[76px] h-[76px] flex flex-col gap-2 items-center justify-center cursor-pointer"
                                    @click="scrollUp"
                                >
                                    <font-awesome-icon :icon="['fas', 'chevron-up']" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="lg:w-1/4 lg:pl-8"><SidebarListRight /></div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import i18next from 'i18next';
import { trans } from 'laravel-vue-i18n';
import _ from 'lodash';
import { TickerTape } from 'vue-tradingview-widgets';
import { mapGetters } from 'vuex';
import api from '../api';
import { formatDateByRegion } from '../helpers.js';
import { useCcm19Global } from '../useCcm19Global.js';

export default {
    components: {
        TickerTape
    },
    setup() {
        const { onReady, tradingView } = useCcm19Global();
        onReady(() => {});

        return { tradingView };
    },

    computed: {
        ...mapGetters('post', {
            post: 'get'
        }),

        // Check if the post is a finanzbildung article
        is_finanzbildung_article() {
            if (this.post && this.post.postCategories && this.post.postCategories.data) {
                return this.post.postCategories.data.map((category) => category.name).includes('Finanzbildung');
            }

            return false;
        },

        authorProfileUrl() {
            const user = this.post.user;

            if (!user) {
                return '';
            }

            return `${window.location.origin}/hkcmnews/?author=${user.id}&author_name=${encodeURIComponent(`${user.name}`)}`;
        },

        tickerTapeOptions() {
            return {
                symbols: [
                    { proName: 'FOREXCOM:SPXUSD', title: 'S&P 500' },
                    { proName: 'BITSTAMP:BTCUSD', title: 'Bitcoin' },
                    { proName: 'BITSTAMP:ETHUSD', title: 'Ethereum' },
                    { description: 'DAX', proName: 'XETR:DAX' },
                    { description: 'Gold', proName: 'TVC:GOLD' },
                    { description: i18next.t('plain_silver'), proName: 'TVC:SILVER' },
                    { description: 'WTI', proName: 'TVC:USOIL' },
                    { description: 'EUR/USD', proName: 'FX:EURUSD' }
                ],
                showSymbolLogo: true,
                colorTheme: 'light',
                isTransparent: false,
                displayMode: 'adaptive',
                locale: 'de'
            };
        }
    },

    watch: {
        /**
         * Watch the route path to update the tradingroomSlug data.
         * E.g. when a popular/related post is clicked the slug gets updated.
         */
        '$route.fullPath': function () {
            this.$route.params.id ? (this.tradingroomSlug = '/' + this.$route.params.id) : (this.tradingroomSlug = null);
        },

        /**
         * An updated tradingroomSlug triggers the content loading.
         * The new post content get displayed on the page.
         */
        tradingroomSlug: function () {
            if (this.$route.params.id) {
                this.getContentstream();
            }
        }
    },

    mounted() {
        this.fetchAuthors();
        this.tradingroomSlug = '/' + this.$route.params.id;

        this.getContentstream();
        this.indexContentstreams({ key: 'related_posts' });

        window.addEventListener('scroll', this.scrollPercentage);
    },

    beforeUnmount() {
        window.removeEventListener('scroll', this.scrollPercentage);
    },

    methods: {
        formatDateByRegion,
        paginate(page) {
            this.indexContentstreams({
                page,
                key: 'related_posts'
            });
        },

        /**
         * Loads the content of a specific post
         * Gets triggered on watch event.
         * The this.tradingroomSlug (watch func.) is the title of the post (as part of the URL)
         * Calls the set-mutation function to update the state with the response.data.
         */
        getContentstream() {
            if (this.loading === false) {
                this.loading = true;

                api.get(`/api/v1/slugs${this.tradingroomSlug}`)
                    .then((response) => {
                        const store = response.data.meta.resource_class_name.toLowerCase();
                        this.$store.commit(`${store}/set`, response.data);

                        document.title = this.$translate(response.data.data.meta_title);
                        document.meta = this.$translate(response.data.data.meta_description);

                        this.loading = false;
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        },

        scrollPercentage() {
            this.currentScrollPercentage = this.$onScreenPercentage(document.getElementById('post'), window);
        },

        async fetchAuthors() {
            try {
                await this.$store.dispatch('authors/index');
                this.authors = this.$store.getters['authors/all'];
            } catch (error) {
                console.error('Error loading team data:', error);
            } finally {
                this.loading = false;
            }
        },

        async indexContentstreams(params) {
            const config = {
                params: {
                    'sort[by]': 'created_at',
                    'sort[order]': 'desc'
                }
            };

            _.merge(config.params, params);

            return await this.$store.dispatch('posts/index', config);
        },

        copy_link() {
            // navigator clipboard api needs a secure context (https)
            if (navigator.clipboard && window.isSecureContext) {
                // navigator clipboard api method'
                navigator.clipboard.writeText(this.current_url);
            } else {
                // hidden text area method
                this.$refs.copy_link.select();
                document.execCommand('copy');
            }

            this.$toast.success(trans('Link copied succesfully'));
        },

        scrollUp() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    },

    data: () => ({
        search: null,
        loading: false,
        activeTopPost: 0,
        authors: [],
        tradingroomSlug: null,
        currentScrollPercentage: 0,
        current_url: window.location.href,
        toggleSocial: false,
        url: window.location.href
    })
};
</script>
<style>
#tradingroom-post .content h2 {
    font-size: clamp(1.1932426932522988rem, calc(1.1932426932522988rem + (1.369306393762915 - 1.1932426932522988) * ((100vw - 20rem) / (96 - 20))), 1.369306393762915rem);
}
</style>
